<template>
  <div class="trends-widget">
    <div
      class="trend-block"
      v-for="item in items"
      :key="item.key"
    >
      <i
        :class="item.iconClass"
        class="main-icon"
        :style="{ color: item.color }"
      />
      <div class="trend-details">
        <h6 :style="{ color: item.color }">{{ item.title }}</h6>
        <span
          v-if="item.hasValue"
          class="badge"
          :class="item.change >= 0 ? 'badge-up' : 'badge-down'"
          :style="{ background: item.change >= 0 ? item.color : '#d9534f' }"
        >
          {{ changeText(item.change) }}
        </span>
        <span v-else class="badge badge-none">—</span>
        <p class="explanation">{{ trendText(item.explanation) }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import { detection_zone_palettes } from '@/constants.js'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'
import { trendText } from '@/i18n/trend'

const props = defineProps({
  trendTotal:   { type: Object, required: true },
  // Walkbys/shorts/clears are dwell-segmentation trends — not applicable in
  // tripwire-only mode, so they're allowed to be null there.
  trendWalkbys: { type: Object, default: null },
  trendShorts:  { type: Object, default: null },
  trendClears:  { type: Object, default: null },
  zoneColor:   { type: String, required: true },
  timeSpan:   { type: String, required: true },
  detectionZoneTypeName:   { type: String, required: true },
  durationSegmentationStrategy:   { type: String, required: true },
  // When true (tripwire-only zones), only the Total trend block is rendered.
  hideSegmentationTrends: { type: Boolean, default: false },
  // Title override for the Total block (e.g. "Total Inbound Trend" for
  // non-Entrance/Exit/CEE tripwire zones). Defaults to the visitor wording.
  // Empty → the translated 'Total Visitor Trend'.
  totalTrendLabel: { type: String, default: '' },
})

const { t } = useI18n()

// "-24.3%" in English exactly as before; Dutch "-24,3%".
function changeText(change) {
  if (currentLanguage() === 'en') return `${change}%`
  return `${formatNumber(change, { decimals: Math.min(2, (String(change).split('.')[1] || '').length) })}%`
}

// A percentage is shown ONLY when the backend could establish a trend. When it
// could not, the badge is a dash — same as the store-level tile. A number on
// screen reads as a finding no matter what the sentence beside it says, so an
// unprovable movement gets no number at all.
//
// The figure itself is the change across the SELECTED PERIOD ("-24%"), not a
// per-day rate: -0.12%/day is unreadable, "-35% over the past year" is the
// question the store is actually asking.
function hasValue(tr) {
  if (!tr) return false
  // is_significant is authoritative. Older payloads (and demo fixtures
  // exported before it existed) don't carry it — fall back to showing
  // whatever figure they do have, which is the pre-change behaviour.
  if (tr.is_significant === false) return false
  return (tr.total_change_pct ?? tr.percent_change_per_day) != null
}

function changeOf(tr) {
  if (!tr) return 0
  return tr.total_change_pct ?? tr.percent_change_per_day ?? 0
}

// choose an icon based on how big the move over the period was
function pickIcon(tr) {
  if (!hasValue(tr)) return 'bi bi-dash-circle'
  const pct = changeOf(tr)
  if (pct > 15)  return 'bi bi-arrow-up-circle'
  if (pct > 2)   return 'bi bi-arrow-up-right-circle'
  if (pct < -15) return 'bi bi-arrow-down-circle'
  if (pct < -2)  return 'bi bi-arrow-down-right-circle'
  return 'bi bi-dash-circle'
}

const palette = computed(() => {
      return detection_zone_palettes[ props.detectionZoneTypeName ] ?? {
        segmentation: {
          walkBy:      '#B2C149',
          shortLinger: '#4F7EB3',
          clearLinger: '#425461'
        }
      }
    })

const items = computed(() => {
  const insufficient = t('charts.trend.insufficientData')
  const totalItem = {
    key: 'total',
    title: `${props.totalTrendLabel || t('charts.trend.totalVisitor')} - ${props.timeSpan}`,
    change: changeOf(props.trendTotal),
    hasValue: hasValue(props.trendTotal),
    explanation: props.trendTotal.explanation,
    iconClass: pickIcon(props.trendTotal),
    color: props.zoneColor
  }

  // Tripwire-only zones: just the Total block, no dwell sub-trends.
  if (props.hideSegmentationTrends) return [totalItem]

  const all = [
    totalItem,
    {
      key: 'walkbys',
      title: t('charts.trend.walkBys', { span: props.timeSpan }),
      change: changeOf(props.trendWalkbys),
      hasValue: hasValue(props.trendWalkbys),
      explanation: props.trendWalkbys?.explanation ?? '',
      iconClass: pickIcon(props.trendWalkbys),
      color: palette.value.segmentation.walkBy
    },
    {
      key: 'shorts',
      title: t('charts.trend.shortLingerers', { span: props.timeSpan }),
      change: changeOf(props.trendShorts),
      hasValue: hasValue(props.trendShorts),
      explanation: props.trendShorts?.explanation ?? '',
      iconClass: pickIcon(props.trendShorts),
      color: palette.value.segmentation.shortLinger
    },
    {
      key: 'clears',
      title: t('charts.trend.clearLingerers', { span: props.timeSpan }),
      change: changeOf(props.trendClears),
      hasValue: hasValue(props.trendClears),
      explanation: props.trendClears?.explanation ?? '',
      iconClass: pickIcon(props.trendClears),
      color: palette.value.segmentation.clearLinger
    }
  ]
  if (props.durationSegmentationStrategy === 'insufficient_data') {
    return [
      all[0], // the total item
      { ...all[1], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
      { ...all[2], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
      { ...all[3], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
    ]
  }
  if (props.durationSegmentationStrategy === '') {
    return [
      { ...all[0], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
      { ...all[1], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
      { ...all[2], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
      { ...all[3], change: 0, hasValue: false, explanation: insufficient, color: '#ccc' },
    ]
  }

  return all
})

</script>

<style scoped>
.trends-widget {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  align-items: stretch;
}

.trend-block{
  display:flex; align-items:flex-start; gap:.6rem;
  background:#f9fbfd;
  border:1px solid #e6edf3;
  border-radius:10px;
  padding:.8rem .9rem;
}

.main-icon{ font-size:1.6rem; margin-top:.1rem; flex-shrink:0; }

.trend-details{ flex:1; min-width:0; }
.trend-details h6{ margin:0 0 .35rem; font-size:1rem; font-weight:600; color:#425461; }
.badge-none{ background:#c9d2d9; }
.badge{ display:inline-block; color:#fff; border-radius:999px; padding:.15rem .5rem; font-size:.8rem; line-height:1; margin-bottom:.35rem; }
.explanation{ margin:0; font-size:.88rem; color:#6b7a86; overflow-wrap:anywhere; }
</style>